/**
 * Tool Result Registry — pluggable mapping from tool names to React components.
 *
 * When the AI assistant calls a backend tool (e.g., find_courses, browse_aol_content),
 * the tool result is rendered by the component registered for that tool name.
 * This eliminates if/else chains in MessageBlock and makes adding new tool UIs
 * a single registration call — no core rendering changes needed.
 *
 * Usage:
 *   // Register at module scope (e.g., in a registrations file or layout)
 *   registerToolRenderer('find_courses', CourseBrowser);
 *   registerToolRenderer('browse_aol_content', RAGContent);
 *
 *   // In MessageBlock or wherever tool results are rendered
 *   const Renderer = getToolRenderer(toolName);
 *   if (Renderer) return <Renderer data={payload} />;
 */

import type { ComponentType } from 'react';
import type { ToolName } from '@/types/chat';

/**
 * A React component registered to render a tool's result.
 *
 * Each tool renderer has its own prop interface (e.g., CourseBrowser
 * accepts `{ courses: Course[] }`, RagContent accepts `{ data: RAGData }`).
 * The registry stores them as `ComponentType<any>` because the caller
 * is responsible for passing the correct props based on the tool name.
 *
 * Type safety is maintained at the call site — the tool name determines
 * which component is returned, and the caller knows what props to pass.
 */
type ToolRenderer = ComponentType<any>;

/** Internal registry — Map ensures O(1) lookup and prevents duplicates */
const registry = new Map<ToolName, ToolRenderer>();

/**
 * Register a React component to render results for a specific tool.
 *
 * Call this at module scope (not inside a component) so registrations
 * happen once on import. Writer is typed against `ToolName` so the
 * compiler catches typos against the canonical tool enum.
 *
 * Duplicate registration semantics:
 *  - Same component registered twice → no-op (idempotent for hot-reload).
 *  - Different component for the same key → throws in dev/test (fail loud:
 *    something is wrong — V1 and V2 both registering, or a test forgot to
 *    isolate). In production, logs to console.error and overwrites so a
 *    single broken module can't take down the whole UI.
 */
export function registerToolRenderer(
  toolName: ToolName,
  component: ToolRenderer
): void {
  const prev = registry.get(toolName);
  if (prev !== undefined && prev !== component) {
    const msg = `Duplicate tool renderer registration for ${toolName}`;
    if (process.env.NODE_ENV !== 'production') throw new Error(msg);
    console.error(msg);
  }
  registry.set(toolName, component);
}

/**
 * Look up the registered renderer for a tool.
 *
 * Reader stays string-keyed because WebSocket payloads carry raw strings —
 * we narrow at the boundary by checking against `ToolName`. Returns
 * undefined if no renderer is registered; caller should fall back to a
 * generic text display.
 */
export function getToolRenderer(toolName: string): ToolRenderer | undefined {
  return registry.get(toolName as ToolName);
}

/**
 * Get all registered tool names. Useful for debugging and documentation.
 */
export function getRegisteredToolNames(): string[] {
  return Array.from(registry.keys());
}
